import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { ICareersPageReportingFilter } from '@employer/app/modules/reports/reports.interface'
import { ReportNavService } from '@employer/app/services/report-nav.service'
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button'
import {
  E11DynamicFormBuilderComponent,
  E11DynamicFormModule,
  e11FieldInputDatePicker,
  e11FormFor,
  e11FormLayoutFullWidth,
} from '@engineering11/ui-lib/e11-form-builder'
import { format } from 'date-fns'
import { Subscription } from 'rxjs'

@Component({
  selector: 'careers-page-filtering',
  standalone: true,
  imports: [E11DynamicFormModule, E11ButtonModule],
  template: `
    <div class="e11-flex e11-justify-between e11-items-start e11-mb-6">
      <div><span class="e11-text-skin-base e11-font-bold">Filters</span></div>
      <a role="button" (click)="resetFilters()" class="e11-text-skin-primary-accent hover:e11-text-skin-primary e11-flex e11-items-center"
        ><span class="e11-mr-1 e11-text-sm">Clear All</span> <span class="material-icons-outlined md-18">refresh</span></a
      >
    </div>
    <e11-dynamic-form-builder
      #formRef
      [fieldGroups]="form"
      [initialValue]="initialValue"
      [hideSubmitMenu]="true"
      (valueChanges)="valueChanges($event)"
    ></e11-dynamic-form-builder>
  `,
})
export class CareersPageFilteringComponent implements OnInit, OnDestroy {
  @ViewChild(E11DynamicFormBuilderComponent) formRef!: E11DynamicFormBuilderComponent<ICareersPageReportingFilter>

  form = e11FormFor<ICareersPageReportingFilter>()([
    e11FormLayoutFullWidth([
      e11FieldInputDatePicker('startDate', 'Start Date', undefined, {
        validatorOrOpts: [],
        extraInputOption: {
          maxYearSelectable: new Date().getFullYear(),
          classOverride: 'e11-border-skin-primary-accent e11-text-skin-primary-accent e11-mb-4',
          showLeadingIcon: true,
          showTrailingIcon: true,
        },
      }),
    ]),
    e11FormLayoutFullWidth([
      e11FieldInputDatePicker('endDate', 'End Date', undefined, {
        validatorOrOpts: [],
        extraInputOption: {
          maxYearSelectable: new Date().getFullYear(),
          classOverride: 'e11-border-skin-primary-accent e11-text-skin-primary-accent e11-mb-4',
          showLeadingIcon: true,
          showTrailingIcon: true,
        },
      }),
    ]),
  ])

  initialValue: ICareersPageReportingFilter | undefined
  routeSub: Subscription | undefined
  isFirstLoad = true

  constructor(private readonly reportNavService: ReportNavService, private readonly router: Router, private readonly route: ActivatedRoute) {
    this.routeSub = this.route.queryParams.subscribe(params => {
      const startDate = params['startDate']
      const endDate = params['endDate']
      const { startDate: formStartDate, endDate: formEndDate } = this.formRef?.form.value ?? {}
      const formattedStartDate = this.formatDate(formStartDate)
      const formattedEndDate = this.formatDate(formEndDate)

      if (startDate !== formattedStartDate || endDate !== formattedEndDate) {
        this.initialValue = {
          startDate: startDate,
          endDate: endDate,
        }
      }
    })
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.routeSub?.unsubscribe()
  }

  valueChanges(event: Record<keyof ICareersPageReportingFilter, string | undefined>) {
    if (!event.endDate && !event.startDate) {
      this.isFirstLoad = false
      this.reportNavService.setCareersPageDateFilter(undefined)
      return
    }

    const startDate = event.startDate ? new Date(event.startDate).toISOString() : undefined
    const endDate = event.startDate && !event.endDate ? new Date().toISOString() : event.endDate

    const formattedStartDate = this.formatDate(startDate)
    const formattedEndDate = this.formatDate(endDate)

    if (!this.isFirstLoad) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        },
        queryParamsHandling: 'merge',
      })
    }

    this.reportNavService.setCareersPageDateFilter({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    })

    this.isFirstLoad = false
  }

  formatDate(date: string | undefined) {
    return date ? format(new Date(date), 'yyyy-MM-dd') : undefined
  }

  resetFilters() {
    this.formRef.form.reset({
      startDate: undefined,
      endDate: undefined,
    })

    // Remove date filter parameters from URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        startDate: null,
        endDate: null,
      },
      queryParamsHandling: 'merge',
    })
  }
}
